import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Mail, MessageCircle, Share2 } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const NAV_LINKS = [
  { href: "/#inicio", label: "Inicio" },
  { href: "/#acerca", label: "Acerca del evento" },
  { href: "/#programa", label: "Programa" },
  { href: "/#talleres", label: "Talleres" },
  { href: "/#faq", label: "Preguntas frecuentes" },
];

export const PublicLayout = ({ children, footerContent = null, contact = null }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const socials = contact ? [
    contact.instagram_url && { label: "Instagram", url: contact.instagram_url },
    contact.facebook_url && { label: "Facebook", url: contact.facebook_url },
    contact.youtube_url && { label: "YouTube", url: contact.youtube_url },
    contact.tiktok_url && { label: "TikTok", url: contact.tiktok_url },
    contact.website_url && { label: "Sitio web", url: contact.website_url },
  ].filter(Boolean) : [];

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (window.location.pathname !== "/") {
        navigate("/", { state: { scrollTo: id } });
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
    }
  };

  return (
    <div className="min-h-screen bg-hz-bg flex flex-col">
      <header className="sticky top-0 z-50 border-b border-hz-gray/50 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" data-testid="nav-logo-link" aria-label="Ir al inicio">
            <Logo variant="title" className="h-9" />
          </Link>
          <nav className="hidden items-center gap-6 lg:flex" aria-label="Navegación principal">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-semibold text-hz-navy transition-colors hover:text-hz-cyan"
              >
                {l.label}
              </button>
            ))}
            <Link
              to="/consulta-registro"
              data-testid="nav-link-consulta"
              className="text-sm font-semibold text-hz-navy transition-colors hover:text-hz-cyan"
            >
              Consultar registro
            </Link>
            <Button
              data-testid="nav-preregister-cta"
              onClick={() => navigate("/registro")}
              className="bg-hz-cyan font-bold text-white hover:bg-hz-blue"
            >
              Pre-regístrate
            </Button>
          </nav>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-mobile-menu-button"
                className="rounded-md p-2 text-hz-navy lg:hidden"
                aria-label="Abrir menú"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-white">
              <div className="mb-8 mt-2">
                <Logo variant="title" className="h-8" />
              </div>
              <nav className="flex flex-col gap-4" aria-label="Navegación móvil">
                {NAV_LINKS.map((l) => (
                  <button
                    key={l.href}
                    onClick={() => go(l.href)}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-left text-base font-semibold text-hz-navy"
                  >
                    {l.label}
                  </button>
                ))}
                <button
                  onClick={() => go("/consulta-registro")}
                  data-testid="mobile-nav-link-consulta"
                  className="text-left text-base font-semibold text-hz-navy"
                >
                  Consultar registro
                </button>
                <Button
                  data-testid="mobile-nav-preregister-cta"
                  onClick={() => go("/registro")}
                  className="mt-2 bg-hz-cyan font-bold text-white hover:bg-hz-blue"
                >
                  Pre-regístrate
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-hz-gray/40 bg-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 md:grid-cols-3">
          <div className="space-y-3">
            {footerContent?.show_logo !== false && <Logo variant="title" className="h-10" />}
            <p className="text-sm font-bold text-hz-navy">Horizonte 2026</p>
            <p className="text-xs font-medium uppercase tracking-widest text-hz-blue">
              Liderazgo con Visión Reformada
            </p>
            {footerContent?.institutional_text && (
              <p className="text-xs leading-relaxed text-hz-ink/70">{footerContent.institutional_text}</p>
            )}
          </div>
          <div className="space-y-3">
            <p className="text-sm font-bold text-hz-navy">Contacto</p>
            {contact?.email && (
              <div className="flex items-center gap-2 text-sm text-hz-ink/70" data-testid="footer-contact-email">
                <Mail className="h-4 w-4 text-hz-cyan" /> {contact.email}
              </div>
            )}
            {contact?.whatsapp && (
              <div className="flex items-center gap-2 text-sm text-hz-ink/70" data-testid="footer-contact-whatsapp">
                <MessageCircle className="h-4 w-4 text-hz-cyan" /> WhatsApp {contact.whatsapp}
              </div>
            )}
            {socials.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-hz-ink/70" data-testid="footer-contact-socials">
                <Share2 className="h-4 w-4 text-hz-cyan" />
                {socials.map((s, i) => (
                  <span key={s.url}>{i > 0 && " · "}<a href={s.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-hz-blue underline-offset-2 hover:underline">{s.label}</a></span>
                ))}
              </div>
            )}
            {!contact?.email && !contact?.whatsapp && socials.length === 0 && (
              <p className="text-xs leading-relaxed text-hz-ink/60" data-testid="footer-contact-pending">Los datos de contacto se publicarán próximamente.</p>
            )}
          </div>
          <div className="space-y-3">
            <p className="text-sm font-bold text-hz-navy">Avisos</p>
            <p className="text-xs leading-relaxed text-hz-ink/70">
              {footerContent?.update_note || "Información sujeta a actualización por el comité organizador."}
            </p>
            <p className="text-xs leading-relaxed text-hz-ink/70">
              {footerContent?.privacy_short || "Aviso de privacidad temporal: tus datos se utilizan exclusivamente para la organización y control de acceso del evento."}
              {footerContent?.privacy_url && (
                <>{" "}<a href={footerContent.privacy_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-hz-blue underline underline-offset-2">Ver aviso completo</a></>
              )}
            </p>
            {footerContent?.terms_url && (
              <p className="text-xs leading-relaxed">
                <a href={footerContent.terms_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-hz-blue underline underline-offset-2">Términos y condiciones</a>
              </p>
            )}
            {(footerContent?.extra_links || []).filter((l) => l.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0)).map((l) => (
              <p key={l.url} className="text-xs leading-relaxed">
                <a href={l.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-hz-blue underline underline-offset-2">{l.label}</a>
              </p>
            ))}
          </div>
        </div>
        <div className="border-t border-hz-gray/40 px-4 py-4 text-center text-xs font-medium text-hz-ink/60">
          <p>{footerContent?.copyright_text || "© 2026 Horizonte 2026 — Liderazgo con Visión Reformada"}</p>
          {footerContent?.staff_access_enabled !== false && (
            <Link to="/login" data-testid="footer-staff-login-link" className="mt-2 inline-block text-hz-navy/60 underline-offset-2 transition-colors hover:text-hz-cyan hover:underline">
              Acceso al equipo organizador
            </Link>
          )}
        </div>
      </footer>
    </div>
  );
};
